// Utility functions for handling map embed links

/**
 * Validates if a string is a valid Google Maps embed code
 * @param {string} embedCode - The potential map embed code to validate
 * @returns {boolean} - Whether the code is a valid Google Maps embed
 */
export const isValidMapEmbed = (embedCode) => {
  if (!embedCode) return false;
  
  // Check if it's an iframe
  const hasIframe = embedCode.includes('<iframe') && embedCode.includes('</iframe>');
  
  // Check if it's from Google Maps
  const isGoogleMaps = embedCode.includes('google.com/maps');
  
  // Check if it has necessary attributes
  const hasRequiredAttrs = embedCode.includes('src="') && 
                          (embedCode.includes('width="') || embedCode.includes('height="'));
  
  return hasIframe && isGoogleMaps && hasRequiredAttrs;
};

/**
 * Extracts coordinates from a Google Maps embed link
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @returns {{lat: number|null, lng: number|null}} - The extracted coordinates
 */
export const extractCoordinatesFromMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return { lat: null, lng: null };
  
  try {
    // Extract the src URL if it's an iframe
    let url = mapEmbedLink;
    const srcMatch = mapEmbedLink.match(/src="([^"]+)"/);
    if (srcMatch && srcMatch[1]) {
      url = srcMatch[1];
    }
    
    // Try different coordinate formats
    const patterns = [
      { regex: /q=(-?\d+\.\d+),(-?\d+\.\d+)/, latIndex: 1, lngIndex: 2 },
      { regex: /center=(-?\d+\.\d+),(-?\d+\.\d+)/, latIndex: 1, lngIndex: 2 },
      { regex: /ll=(-?\d+\.\d+),(-?\d+\.\d+)/, latIndex: 1, lngIndex: 2 },
      { regex: /sll=(-?\d+\.\d+),(-?\d+\.\d+)/, latIndex: 1, lngIndex: 2 },
      { regex: /!2d(-?\d+\.\d+)!3d(-?\d+\.\d+)/, latIndex: 2, lngIndex: 1 },
      { regex: /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/, latIndex: 1, lngIndex: 2 }
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern.regex);
      if (match) {
        return {
          lat: parseFloat(match[pattern.latIndex]),
          lng: parseFloat(match[pattern.lngIndex])
        };
      }
    }
    
    return { lat: null, lng: null };
  } catch (error) {
    console.error('Error extracting coordinates:', error);
    return { lat: null, lng: null };
  }
};

/**
 * Extracts the address from a Google Maps embed link
 * @param {string} mapEmbedLink - The map embed iframe code
 * @returns {string} - The extracted address
 */
export const extractAddressFromMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return '';
  
  try {
    // Extract the src URL from the iframe
    const srcMatch = mapEmbedLink.match(/src="([^"]+)"/);
    if (!srcMatch || !srcMatch[1]) return '';
    
    const srcUrl = srcMatch[1];
    const urlParams = new URL(srcUrl).searchParams;
    
    // Try different possible parameters where the address might be stored
    let address = '';
    
    // Check various parameters in order of likelihood
    const paramChecks = ['q', 'place', 'query'];
    for (const param of paramChecks) {
      address = urlParams.get(param);
      if (address) break;
    }
    
    // If still not found, try parsing from the pb parameter
    if (!address) {
      const pbParam = urlParams.get('pb');
      if (pbParam) {
        const markers = pbParam.match(/!3m3!1m2!1s(.*?)!2s(.*?)!/);
        if (markers && markers[2]) {
          address = decodeURIComponent(markers[2].replace(/\+/g, ' '));
        }
      }
    }
    
    if (!address) return '';
    
    // Clean up the address
    return address
      .replace(/\+\d+\.\d+,\s*-?\d+\.\d+/, '')      // Remove coordinates
      .replace(/\+[\w\d]+\+[\w\d]+/, '')            // Remove plus codes
      .replace(/\++/g, ' ')                          // Replace + with spaces
      .replace(/%20/g, ' ')                          // Replace %20 with spaces
      .replace(/\s{2,}/g, ' ')                       // Remove extra spaces
      .replace(/^[0-9]+\s/, '')                      // Remove leading numbers
      .replace(/[!@#$%^&*()<>?\/|}{]/g, '')         // Remove special characters
      .trim();
    
  } catch (error) {
    console.error('Error extracting address:', error);
    return '';
  }
};

/**
 * Normalizes a map embed link to ensure consistent format
 * @param {string} mapEmbedLink - The map embed iframe code or URL
 * @returns {string} - Normalized iframe code
 */
export const normalizeMapEmbed = (mapEmbedLink) => {
  if (!mapEmbedLink) return '';
  
  try {
    // If it's already an iframe, validate and return
    if (mapEmbedLink.includes('<iframe')) {
      return isValidMapEmbed(mapEmbedLink) ? mapEmbedLink : '';
    }
    
    // If it's just a URL, convert to iframe
    if (mapEmbedLink.includes('google.com/maps')) {
      return `<iframe src="${mapEmbedLink}" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
    }
    
    return '';
  } catch (error) {
    console.error('Error normalizing map embed:', error);
    return '';
  }
}; 